import { Plan, PricingVariant } from '@wix/ambassador-pricing-plans-v3-plan/types';
import { PricingVariantCategory } from '@wix/ambassador-pricing-plans-v3-pricing-variant-category/types';
import { DefaultSettingsAdapter } from '../components/PackagePicker/DefaultSettingsAdapter';
import { PlansInTabsViewState, PlansInTabsViewWriter } from '../services/plans-in-tabs-view-storage';
import { safeParse } from './common';
import { AnyPlan } from './plan-unified-utils';
import { getServerVisibleVariants, VariantsByPlan } from './variant-visibility';

export type VariantCategoryNameById = Record<
  NonNullable<PricingVariant['categoryIds']>[number],
  NonNullable<PricingVariantCategory['name']>
>;

export function buildVariantCategoryNameById(categories: PricingVariantCategory[]): VariantCategoryNameById {
  return Object.fromEntries(categories.map((category) => [category.id!, category.name!]));
}

export function getCategorizedVisibleVariants(
  plan: Plan,
  variantCategoryNameById: VariantCategoryNameById,
): PricingVariant[] {
  return getServerVisibleVariants(plan).filter((variant) =>
    variant.categoryIds?.some((categoryId) => variantCategoryNameById[categoryId]),
  );
}

function toTabCategoryIds(categoryIds: string[], variantCategoryNameById: VariantCategoryNameById): string[] {
  return categoryIds.reduce<string[]>((tabCategoryIds, categoryId) => {
    const hasCategoryName = Boolean(variantCategoryNameById[categoryId]);
    const isAlreadyAdded = tabCategoryIds.includes(categoryId);

    if (!hasCategoryName || isAlreadyAdded) {
      return tabCategoryIds;
    }

    return [...tabCategoryIds, categoryId];
  }, []);
}

export function getPlanListEntriesForTab({
  plans,
  activeCategoryId,
  plansInTabsView,
  selectedCategoryIds,
  variantCategoryNameById,
}: {
  plans: Plan[];
  activeCategoryId: string;
  plansInTabsView: string[];
  selectedCategoryIds: string[];
  variantCategoryNameById: VariantCategoryNameById;
}): {
  plans: Plan[];
  variantEntries: VariantsByPlan;
} {
  const allPlansById = new Map(plans.map((plan) => [plan.id!, plan]));

  const eligibleInUserOrder = plansInTabsView.flatMap((planId) => {
    const plan = allPlansById.get(planId);

    if (!plan || !getIsPlanEligibleForTabsView(plan, selectedCategoryIds, variantCategoryNameById)) {
      return [];
    }

    const matchingVariant = getCategorizedVisibleVariants(plan, variantCategoryNameById).find((variant) =>
      variant.categoryIds?.includes(activeCategoryId),
    );

    return matchingVariant ? [{ plan, variant: matchingVariant }] : [];
  });

  return {
    plans: eligibleInUserOrder.map(({ plan }) => plan),
    variantEntries: Object.fromEntries(eligibleInUserOrder.map(({ plan, variant }) => [plan.id!, [variant]])),
  };
}

export function deriveTabCategoryIds(plans: Plan[], variantCategoryNameById: VariantCategoryNameById): string[] {
  const categoryIds = plans.flatMap((plan) => {
    const categorizedVisibleVariants = getCategorizedVisibleVariants(plan, variantCategoryNameById);

    return categorizedVisibleVariants.length < 2
      ? []
      : categorizedVisibleVariants.flatMap((variant) => variant.categoryIds ?? []);
  });

  return toTabCategoryIds(categoryIds, variantCategoryNameById);
}

export function getIsPlanEligibleForTabsView(
  plan: Plan,
  selectedCategoryIds: string[],
  variantCategoryNameById: VariantCategoryNameById,
): boolean {
  const categorizedVisibleVariants = getCategorizedVisibleVariants(plan, variantCategoryNameById);

  if (categorizedVisibleVariants.length < 2) {
    return false;
  }

  const visibleVariantCategoryIds = categorizedVisibleVariants.flatMap((variant) => variant.categoryIds ?? []);

  return selectedCategoryIds.every((categoryId) => visibleVariantCategoryIds.includes(categoryId));
}

export function pruneStaleTabIds(availableCategoryIds: string[], selectedCategoryIds: string[]): string[] {
  return selectedCategoryIds.filter((categoryId) => availableCategoryIds.includes(categoryId));
}

export function pruneStaleVisibleTabs(
  plans: AnyPlan[],
  adaptedSettings: DefaultSettingsAdapter,
  variantCategoryNameById: VariantCategoryNameById,
): string[] {
  const selectedTabs = safeParse<string[]>(adaptedSettings.get('visibleTabs'), []);

  if (!selectedTabs.length) {
    return [];
  }

  const availableCategoryIds = deriveTabCategoryIds(plans as Plan[], variantCategoryNameById);
  const prunedSelectedTabs = pruneStaleTabIds(availableCategoryIds, selectedTabs);

  if (prunedSelectedTabs.length !== selectedTabs.length) {
    adaptedSettings.set('visibleTabs', JSON.stringify(prunedSelectedTabs));
  }

  return prunedSelectedTabs;
}

export function getOrderedVisibleTabsViewPlans({
  plans,
  plansInTabsViewState,
  isWhitelist,
}: {
  plans: AnyPlan[];
  plansInTabsViewState: PlansInTabsViewState;
  isWhitelist: boolean;
}): AnyPlan[] {
  const visiblePlans = isWhitelist
    ? plans.filter((plan) => plansInTabsViewState.plansInTabsView.includes(plan.id!))
    : plans.filter((plan) => !plansInTabsViewState.hiddenPlansInTabsView.includes(plan.id!));

  return orderPlansByStoredOrder(visiblePlans, plansInTabsViewState.planOrderInTabsView);
}

export function orderPlansByStoredOrder(plans: AnyPlan[], planOrderInTabsView: string[]): AnyPlan[] {
  const orderedPlans = planOrderInTabsView.flatMap((planId) => {
    const plan = plans.find(({ id }) => id === planId);

    return plan ? [plan] : [];
  });
  const unorderedPlans = plans.filter((plan) => !planOrderInTabsView.includes(plan.id!));

  return [...orderedPlans, ...unorderedPlans];
}

export function getPlanTabCategoryIds(plan: Plan, variantCategoryNameById: VariantCategoryNameById): string[] {
  const categoryIds = getCategorizedVisibleVariants(plan, variantCategoryNameById).flatMap(
    (variant) => variant.categoryIds ?? [],
  );

  return toTabCategoryIds(categoryIds, variantCategoryNameById);
}

export async function pruneStalePlansInTabsView({
  plans,
  selectedCategoryIds,
  variantCategoryNameById,
  writer,
  isWhitelist,
}: {
  plans: AnyPlan[];
  selectedCategoryIds: string[];
  variantCategoryNameById: VariantCategoryNameById;
  writer: PlansInTabsViewWriter;
  isWhitelist: boolean;
}): Promise<PlansInTabsViewState> {
  const allowedPlanIds = plans
    .filter((plan) => getIsPlanEligibleForTabsView(plan as Plan, selectedCategoryIds, variantCategoryNameById))
    .map((plan) => plan.id!);

  return isWhitelist
    ? writer.pruneWhitelistPlanIds({ allowedPlanIds })
    : writer.pruneBlacklistPlanIds({ allowedPlanIds });
}
